import { Currencies } from '@/entities/curency'
import { convertEmptyStringsToNull } from '@/features/farm'
import {
  formatCardNumber,
  NewRequisite,
  useAddRequisiteForm,
  useRequisiteOptions,
} from '@/features/requisites'
import CardIcon from '@/shared/assets/icons/card.svg?react'
import ArrowUpIcon from '@/shared/assets/icons/detail/arrow-up.svg?react'
import AccountIcon from '@/shared/assets/icons/document.svg?react'
import SimCardIcon from '@/shared/assets/icons/simСard.svg?react'
import TrashIcon from '@/shared/assets/icons/trash.svg?react'
import { BaseButton, BaseDialog, BaseInput, BaseSelect, BaseTooltip } from '@/shared/components'
import { Plus } from 'lucide-react'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'

type Props = {
  modalVisible: boolean
  setModalVisible: (value: boolean) => void
  onSubmit: (data: NewRequisite) => void
  isLoading?: boolean
  onSuccess?: (requisiteId: string) => void
}

export const DialogAddRequisite = ({
                                     modalVisible,
                                     setModalVisible,
                                     onSubmit,
                                     isLoading,
                                   }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<NewRequisite>({
    defaultValues: {
      currency: Currencies.RUB,
      minTimeoutBetweenDealsInMinutes: 0,
      createPaymentRequisitRequests: [],
    },
  })

  const { optionsBanks, optionsDevice, optionsFarm } = useRequisiteOptions(modalVisible)

  const {
    selectedBankId,
    showAdditionalFields,
    phone,
    account,
    cards,
    sbpPaymentLink,
    phoneError,
    accountError,
    cardsErrors,
    handlePhoneChange,
    handleAccountChange,
    handleCardChange,
    handleSbpPaymentLinkChange,
    addCard,
    removeCard,
    validateForm,
    resetForm,
    initializeForBank,
    fieldsToShow,
    buildRequisites,
  } = useAddRequisiteForm({ setValue })

  const bankId = watch('bankId')
  const fieldsConfig = fieldsToShow

  // Устанавливаем реквизиты в зависимости от выбранного банка
  useEffect(() => {
    if (bankId) {
      initializeForBank(bankId)
    }
  }, [bankId, initializeForBank])

  // Обновление реквизитов при изменении данных
  useEffect(() => {
    if (selectedBankId) {
      setValue('createPaymentRequisitRequests', buildRequisites())
    }
  }, [selectedBankId, setValue, buildRequisites])

  const handleClose = () => {
    reset()
    resetForm()
    setModalVisible(false)
  }

  const onFormSubmit = (data: NewRequisite) => {
    const processedData = convertEmptyStringsToNull({
      ...data,
      sbpPaymentLink: sbpPaymentLink || null,
    })

    if (!validateForm()) {
      return
    }

    onSubmit(processedData)
    console.log('createData', data)
    console.log("",buildRequisites)
  }

  useEffect(() => {
    if (modalVisible) {
      resetForm()
    }
  }, [modalVisible, resetForm])


  return (
    <BaseDialog
      isVisible={modalVisible}
      onClose={handleClose}
      onAction={handleSubmit(onFormSubmit)}
      title="Добавление реквизита"
      isLoading={isLoading}
    >
      <div className="max-h-[600px] overflow-y-auto scrollbar-hide">
        <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col gap-y-4 p-0.5">
          <Controller
            name="deviceId"
            control={control}
            rules={{ required: 'Необходимо выбрать устройство' }}
            render={({ field, fieldState }) => (
              <BaseSelect
                {...field}
                label="Устройство"
                placeholder="Выберите"
                options={optionsDevice}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="bankId"
            control={control}
            rules={{ required: 'Необходимо выбрать банк' }}
            render={({ field, fieldState }) => (
              <BaseSelect
                {...field}
                label="Банк"
                placeholder="Выберите"
                options={optionsBanks}
                error={fieldState.error?.message}
              />
            )}
          />

          <div
            className={`flex flex-col gap-y-4 transition-all duration-300 ease-in-out ${
              showAdditionalFields ? 'opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
            }`}
          >
            <Controller
              name="farmId"
              control={control}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  label="Прикрепить к ферме"
                  placeholder="Выберите"
                  options={optionsFarm}
                />
              )}
            />
            <BaseInput
              label="Суточный лимит"
              placeholder="2 000 000 RUB"
              variant="secondary"
              type="number"
              error={errors.maxDailyLimit?.message}
              {...register('maxDailyLimit', {
                required: 'Обязательное поле',
                min: { value: 1, message: 'Должно быть больше 0' },
              })}
              toolpitText="Максимальный объём средств, который может быть зачислен на этот реквизит в течение календарных суток (с 00:00 до 23:59 по МСК)."
            />

            <BaseInput
              label="Интервал между сделками (мин.)"
              placeholder="0"
              variant="secondary"
              type="number"
              error={errors.minTimeoutBetweenDealsInMinutes?.message}
              {...register('minTimeoutBetweenDealsInMinutes', {
                min: { value: 0, message: 'Должно быть положительным числом' },
              })}
              toolpitText="Минимальное время в минутах между подтверждёнными сделками на этот реквизит."
            />
            <div className="gap-4 grid grid-cols-2">
              <BaseInput
                label="Мин. сумма"
                placeholder="Мин. сумма"
                variant="secondary"
                type="number"
                error={errors.minDealAmount?.message}
                {...register('minDealAmount', {
                  required: 'Обязательное поле',
                  min: { value: 1, message: 'Должно быть больше 0' },
                })}
                toolpitText="Минимальная сумма сделки, которая может быть создана на данный реквизит."
              />
              <BaseInput
                label="Макс. сумма"
                placeholder="Макс. сумма"
                variant="secondary"
                type="number"
                error={errors.maxDealAmount?.message}
                {...register('maxDealAmount', {
                  required: 'Обязательное поле',
                  min: { value: 1, message: 'Должно быть больше 0' },
                  validate: (value) => {
                    const minValue = Number(watch('minDealAmount'))
                    return !minValue || Number(value) >= minValue || 'Должно быть больше мин. суммы'
                  },
                })}
                toolpitText="Максимальная сумма сделки, допустимая для данного реквизита."
              />
            </div>
            <div className="gap-4 grid grid-cols-3">
              <BaseInput
                label="Фамилия"
                placeholder="Ввести"
                variant="secondary"
                error={errors.personSurname?.message}
                {...register('personSurname', { required: 'Обязательное поле' })}
              />
              <BaseInput
                label="Имя"
                placeholder="Ввести"
                variant="secondary"
                error={errors.personName?.message}
                {...register('personName', { required: 'Обязательное поле' })}
              />
              <BaseInput
                label="Отчество"
                placeholder="Ввести"
                variant="secondary"
                {...register('personMiddlename')}
              />
            </div>
            <div className="flex items-start gap-3 px-3 py-4 border rounded-lg">
              <BaseTooltip text="Подробнее" />
              <span className="text-text-secondary text-sm">
                Убедитесь, что правильно привязали реквизит. После 10 отказов реквизит будет
                остановлен.
              </span>
            </div>

            {/* Поля реквизитов */}
            {selectedBankId && (
              <div className="flex flex-col gap-4">
                {fieldsConfig.showPhone && (
                  <BaseInput
                    label="Номер телефона"
                    placeholder="+7 xxx xxx-xx-xx"
                    variant="secondary"
                    value={phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    leftIcon={<SimCardIcon className="text-text-secondary" />}
                    error={phoneError}
                  />
                )}

                {fieldsConfig.showAccount && (
                  <div className="flex gap-[10px] w-full">
                    <ArrowUpIcon />
                    <BaseInput
                      label="Номер счета"
                      placeholder="00000 000 0 0000 0000000"
                      variant="secondary"
                      value={account}
                      onChange={(e) => handleAccountChange(e.target.value)}
                      leftIcon={<AccountIcon className="text-text-secondary" />}
                      error={accountError}
                      className="w-full"
                    />
                  </div>
                )}

                {fieldsConfig.showSbpPaymentLink && (
                  <BaseInput
                    label="Озон чаевые"
                    placeholder="Введите ссылку"
                    variant="secondary"
                    value={sbpPaymentLink}
                    onChange={(e) => handleSbpPaymentLinkChange(e.target.value)}
                  />
                )}

                {fieldsConfig.showCards && (
                  <>
                    {cards.map((card, index) => (
                      <BaseInput
                        key={index}
                        label="Номер карты"
                        placeholder="0000 0000 0000 0000"
                        variant="secondary"
                        value={formatCardNumber(card).formatted}
                        onChange={(e) => handleCardChange(e.target.value, index)}
                        leftIcon={<CardIcon className="text-text-secondary" />}
                        rightIcon={
                          <BaseButton variant="ghost" size="icon" onClick={() => removeCard(index)}>
                            <TrashIcon className="text-error" />
                          </BaseButton>
                        }
                        error={cardsErrors[index]}
                      />
                    ))}

                    {cards.length < fieldsConfig.maxCards && (
                      <BaseButton
                        onClick={addCard}
                        variant="ghost"
                        iconPosition="left"
                        icon={<Plus className="text-primary dark:text-primary-dark" />}
                        className="disabled:opacity-40 text-primary dark:text-primary-dark"
                      >
                        Добавить карту
                      </BaseButton>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Скрытые поля для отправки формы */}
            <input type="hidden" {...register('currency')} />
            <input type="hidden" {...register('createPaymentRequisitRequests.0.requisitType')} />
            <input type="hidden" {...register('createPaymentRequisitRequests.1.requisitType')} />
          </div>
        </form>
      </div>
    </BaseDialog>
  )
}